import { CommonModule, NgClass } from '@angular/common';
import { Component, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { AuthService } from '@modules/auth/auth-service';
import { SwalService } from '@shared/services/swal.service';
import { passwordStrengthValidator } from '@modules/auth/validators/password-strength.validator';
import { DialogLayoutComponent } from '@components/dialog-layout/dialog-layout.component';
import { TranslatePipe } from '@shared/i18n/translate.pipe';
import { LocalStorageService } from 'app/storage/local-storage-service';
import { CircleCheck, CircleX, LucideAngularModule } from 'lucide-angular';

@Component({
  selector: 'app-external-change-password-dialog',
  templateUrl: './external-change-password-dialog.component.html',
  styleUrls: ['./external-change-password-dialog.component.scss'],
  imports: [
    CommonModule,
    MatDialogModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    DialogLayoutComponent,
    TranslatePipe,
    LucideAngularModule,
    NgClass,
  ],
})
export class ExternalChangePasswordDialogComponent {
  private readonly dialogRef = inject(MatDialogRef<ExternalChangePasswordDialogComponent>);
  private readonly fb = inject(FormBuilder);
  private readonly authService = inject(AuthService);
  private readonly swalService = inject(SwalService);
  protected readonly data = inject(MAT_DIALOG_DATA, { optional: true });
  private readonly localStorage = inject(LocalStorageService);

  readonly circleCheck = CircleCheck;
  readonly circleX = CircleX;

  submitting = false;
  showCurrentPassword = false;
  showNewPassword = false;
  showConfirmPassword = false;
  passwordStrength = 0;

  form = this.fb.group({
    currentPassword: this.fb.nonNullable.control('', { validators: [Validators.required] }),
    newPassword: this.fb.nonNullable.control('', {
      validators: [Validators.required, passwordStrengthValidator()],
    }),
    confirmPassword: this.fb.nonNullable.control('', { validators: [Validators.required] }),
  });

  toggleShowCurrentPassword(): void {
    this.showCurrentPassword = !this.showCurrentPassword;
  }

  toggleShowNewPassword(): void {
    this.showNewPassword = !this.showNewPassword;
  }

  toggleShowConfirmPassword(): void {
    this.showConfirmPassword = !this.showConfirmPassword;
  }

  close(): void {
    this.dialogRef.close();
  }

  checkStrength(password: string) {
    let score = 0;
    if (/[A-Z]/.test(password)) score++;
    if (/[a-z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[^A-Za-z0-9\s]/.test(password)) score++;
    if (password.length >= 8) score++;
    this.passwordStrength = (score / 5) * 100;
  }

  submit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const currentPassword = this.form.controls.currentPassword.value;
    const newPassword = this.form.controls.newPassword.value;
    const confirmPassword = this.form.controls.confirmPassword.value;

    if (newPassword !== confirmPassword) {
      this.form.controls.confirmPassword.setErrors({ mismatch: true });
      this.form.controls.confirmPassword.markAsTouched();
      return;
    }

    this.submitting = true;
    this.authService
      .changePassword({
        currentPassword,
        newPassword,
        confirmPassword,
      })
      .subscribe({
        next: (response) => {
          this.submitting = false;
          const res = response.data?.changePassword;
          if (res?.status) {
            this.swalService.successToast(res.message ?? 'Password changed successfully');
            const profile = this.localStorage.getUserProfile();
            if (profile) {
              profile.requireNewPassword = false;
              this.localStorage.saveUserProfile(profile);
            }
            this.dialogRef.close({ success: true });
            return;
          }
          this.swalService.error(res?.message ?? 'Failed to change password');
        },
        error: (error) => {
          this.submitting = false;
          this.swalService.error(error?.message ?? 'Failed to change password');
        },
      });
  }
}
